"use client";

import { createContext, useContext, useState, ReactNode } from 'react';
import {  iProComponent, iProProjectMain } from '../models/interfaceProject';
import { iMyDoc } from '../models/interfaceMyDoc';
import { XCircle } from 'lucide-react';
import { AccessType, AlertType, TypeEnvironment, TypeLayout } from '../constants/enums';
import { iUserLoginInfo } from '../models/interfaceUserLogin';
import { Conversation } from '../models/interfaceConversation';
import { iQueryParams } from '../models/interfaceQueryParams';

interface UserContextType  {
  typeLayout: TypeLayout;
  setTypeLayout: (data: any) => void; 
  showHeader: boolean;
  setShowHeader:(data: any) => void;

  user: any;
  setUser: (data: any) => void; 
  userDrive: any;
  setUserDrive: (data: any) => void; 

  accessType:AccessType;
  setAccessType:(data: any) => void;
  userLoading:boolean;
  setUserLoading: (data: any) => void;
  userLoaded:boolean;
  setUserLoaded: (data: any) => void;

  userLoginInfo:iUserLoginInfo;
  setUserLoginInfo:(data: any) => void;

  userProject: iProProjectMain;
  setUserProject: (data: any) => void;
  userProjectLoading:boolean;
  setUserProjectLoading: (data: any) => void;
  userProjectLoaded:boolean;
  setUserProjectLoaded: (data: any) => void;


  myDocs: iMyDoc[];
  setMyDocs: (data: any) => void;
  myDocsLoading:boolean;
  setMyDocsLoading: (data: any) => void;

  activePanel:"left" | "right" | null;
  setActivePanel: (data: any) => void;

  modeLayout:"edit" | "view" | "published";
  setModeLayout: (data: any) => void;

  formSchemaJson:iProComponent[];
  setFormSchemaJson: (data: any) => void;

  currentConversation: Conversation | null;
  setCurrentConversation: (data: any) => void;
  changeConversation: boolean;
  setChangeConversation: (data: any) => void;  

  listConversation: Conversation[];
  setListConversation: (data: any) => void;  
  listConversationLoading:boolean;
  setListConversationLoading: (data: any) => void;
  addConversation: boolean;
  setAddConversation: (data: any) => void;

  addAlert: (message: string, type?: AlertType, duration?: number) => void;

}
const UserContext = createContext<UserContextType | undefined>(undefined);

 
interface Alert {
  id: string;
  message: string;
  type?: AlertType;
  duration?: number;
}

// Hook para usar el contexto de usuario
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
      throw new Error('useUser must be used within a UserProvider');
    }
    return context; 
 };

 
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [typeLayout, setTypeLayout] = useState<TypeLayout>(TypeLayout.Personal); 
  const [showHeader, setShowHeader] = useState(false); 
  
  const [user, setUser] = useState<any>(null);
  const [userDrive, setUserDrive] = useState<any>(null);
  const [accessType,setAccessType] = useState<AccessType>(AccessType.LogIn);
  const [userLoading, setUserLoading] = useState(false); 
  const [userLoginInfo, setUserLoginInfo] = useState<any>(null); 

  const [userProject, setUserProject] = useState<any>(null);
  const [userProjectLoading, setUserProjectLoading] = useState(false); 
  const [userProjectLoaded, setUserProjectLoaded] = useState(false); 

  const [myDocs, setMyDocs] = useState<any>(null);
  const [myDocsLoading, setMyDocsLoading] = useState(false);   
  const [userLoaded, setUserLoaded] = useState(false);
  const [activePanel, setActivePanel] = useState<"left" | "right" | null>(null);
  
  const [modeLayout, setModeLayout] = useState<"edit" | "view" | "published">("view");

  const [currentConversation,setCurrentConversation] = useState<Conversation| null>(null);
  const [changeConversation,setChangeConversation] = useState(false);
  const [listConversation,setListConversation] = useState<Conversation[]>([]);
  const [listConversationLoading, setListConversationLoading] = useState(false); 
  const [addConversation, setAddConversation] = useState(false); 
 

 
  
  const [formSchemaJson, setFormSchemaJson] = useState<iProComponent[]>( [] );
  
  // ALERTAS
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const addAlert = (message: string, type?: AlertType, duration: number = 5000) => {
    const newAlert: Alert = { id: crypto.randomUUID(), message, type, duration };
    setAlerts((prev) => [...prev, newAlert]); 
    setTimeout(() => {
      removeAlert(newAlert.id);
    }, duration);
  };

  const removeAlert = (id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

    return (
      <UserContext.Provider value={{ 
                                    typeLayout, setTypeLayout, 
                                    showHeader, setShowHeader,
                                    user, setUser,
                                    userDrive, setUserDrive,
                                    accessType,setAccessType,
                                    userLoading, setUserLoading,
                                    userLoaded, setUserLoaded,
                                    userLoginInfo, setUserLoginInfo,
                                    userProject,setUserProject,
                                    userProjectLoading, setUserProjectLoading,
                                    userProjectLoaded, setUserProjectLoaded,
                                    
                                    myDocs, setMyDocs,
                                    myDocsLoading,setMyDocsLoading,
                                    
                                    activePanel, setActivePanel,                                    
                                    modeLayout, setModeLayout,
                                    
                                    formSchemaJson, setFormSchemaJson,
                                    currentConversation,setCurrentConversation,
                                    changeConversation,setChangeConversation,
                                    listConversation,setListConversation,
                                    listConversationLoading, setListConversationLoading,
                                    addConversation, setAddConversation,
                                    addAlert
                                    }}>
        {children}

        <div className="fixed bottom-5 left-1/2 transform -translate-x-1/2 w-80 flex flex-col space-y-2  z-[99999]">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`flex items-center justify-between p-3 rounded-lg shadow-md opacity-90 transition-all duration-500 ease-in-out ${getAlertStyle(alert.type)}`}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-sm font-medium">{alert.message}</span>
            <button onClick={() => removeAlert(alert.id)} className="text-black/50 hover:text-black transition">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>

      </UserContext.Provider>
    );
  };


  const getAlertStyle = (type?: AlertType) => {
    switch (type) {
      case "success":
        return "bg-green-100 text-green-700 border border-green-200";
      case "warning":
        return "bg-yellow-100 text-yellow-700 border border-yellow-200";
      case "error":
        return "bg-red-100 text-red-700 border border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };


 /*
  const getAlertStyle = (type?: AlertType) => {
    switch (type) {
      case "success":
        return "bg-green-200 text-green-800 border border-green-300";
      case "warning":
        return "bg-yellow-200 text-yellow-800 border border-yellow-300";
      case "error":
        return "bg-red-200 text-red-800 border border-red-300";
      default:
        return "bg-gray-200 text-gray-800 border border-gray-300";
    }
  };*/